import React, { useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
} from "@heroui/react";

export default function CreateNotificationFormModal({ onSave, onClose }) {
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({});
  const [fileName, setFileName] = useState("");
  const [errors, setErrors] = useState({});

  const types = [
    { key: "ev_news", label: "EV News & Policy" },
    { key: "new_launches", label: "New Launches" },
    { key: "offers_discounts", label: "Offers / Discounts" },
    { key: "upcoming_vehicles", label: "Upcoming Vehicles" },
  ];

  const titles = {
    ev_news: "Create EV News & Policy Notification",
    new_launches: "Create New Launch Notification",
    offers_discounts: "Create Offer / Discount Notification",
    upcoming_vehicles: "Create Upcoming Vehicle Notification",
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image" && files?.[0]) {
      setFormData({ ...formData, image: files[0] });
      setFileName(files[0].name);
      setErrors((prev) => ({ ...prev, image: "" }));
    } else {
      setFormData({ ...formData, [name]: value });
      if (value.trim()) setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title?.trim()) newErrors.title = "This field is required";
    if (!formData.description?.trim()) newErrors.description = "This field is required";
    if (showImageUpload && !formData.image) newErrors.image = "This field is required";
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    onSave(selectedType, formData);
  };

  const showImageUpload = selectedType && ["new_launches", "offers_discounts"].includes(selectedType);

  return (
    <Modal
      isOpen
      onOpenChange={onClose}
      backdrop="blur"
      size={selectedType ? "2xl" : "md"}
      classNames={{
        base: "transition-all duration-300 ease-in-out"
      }}
    >
      <ModalContent className="bg-white rounded-lg shadow-2xl">
        <ModalHeader className="flex items-center gap-1">
          {selectedType && (
            <Button
              isIconOnly
              variant="light"
              onPress={() => {
                setSelectedType(null);
                setFormData({});
                setFileName("");
                setErrors({});
              }}
              size="sm"
              className="mr-1 text-gray-500 hover:text-gray-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2.5}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </Button>
          )}
          <h2 className="text-2xl font-medium text-gray-800">
            {selectedType ? titles[selectedType] : "Create Notification"}
          </h2>
        </ModalHeader>

        <ModalBody>
          {!selectedType ? (
            <div className="pb-4">
              <p className="text-gray-600 mb-4">
                Select the type of notification you want to create.
              </p>
              <div className="grid grid-cols-1 gap-3">
                {types.map((type) => (
                  <Button
                    type="button"
                    key={type.key}
                    size="lg"
                    onClick={() => setSelectedType(type.key)}
                    className="p-4 rounded-lg bg-[#E9E9E9] text-left transition-all border border-transparent hover:border-gray-300 flex items-center justify-between group"
                  >
                    <span className="font-semibold text-gray-800">
                      {type.label}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="w-5 h-5 text-gray-400 group-hover:text-gray-700 transition-colors"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <form id="create-notification-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <Input
                  name="title"
                  value={formData.title || ""}
                  onChange={handleChange}
                  className={`w-full rounded-md  ${errors.title ? 'border-red-500' : ''}`}
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <Textarea
                  name="description"
                  value={formData.description || ""}
                  onChange={handleChange}
                  minRows={5}
                  style={{ resize: 'vertical' }}
                  className={`w-full rounded-md  ${errors.description ? 'border-red-500' : ''}`}
                ></Textarea>
                {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
              </div>

              {/* Image Upload */}
              {showImageUpload && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Image
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed border-gray-300 rounded-md">
                    <div className="space-y-1 text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <label className="relative cursor-pointer text-button font-medium">
                        <span>Upload an image</span>
                        <Input
                          type="file"
                          name="image"
                          accept="image/*"
                          className="sr-only"
                          onChange={handleChange}
                        />
                      </label>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF up to 10MB
                      </p>
                    </div>
                  </div>
                  {fileName && (
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      Selected file:{" "}
                      <span className="font-medium">{fileName}</span>
                    </p>
                  )}
                  {errors.image && <p className="text-red-500 text-xs mt-1">{errors.image}</p>}
                </div>
              )}

              {/* Link URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link URL
                </label>
                <Input
                  name="link"
                  type="url"
                  placeholder="https://example.com"
                  value={formData.link || ""}
                  onChange={handleChange}
                  className="w-full rounded-md shadow-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  If left blank, defaults to https://evindia.online
                </p>
              </div>
            </form>
          )}
        </ModalBody>

        <ModalFooter className="flex justify-end gap-3 pt-4">
          {!selectedType ? (
            <Button
              variant="flat"
              color="default"
              onPress={onClose}
              className="px-4 py-2 rounded-lg"
            >
              Cancel
            </Button>
          ) : (
            <>
              <Button
                variant="flat"
                color="default"
                onPress={() => {
                  setSelectedType(null);
                  setFormData({});
                  setFileName("");
                  setErrors({});
                }}
                className="px-4 py-2 rounded-lg"
              >
                Back
              </Button>
              <Button
                className="px-4 py-2 bg-button text-white rounded-lg font-semibold"
                type="submit"
                form="create-notification-form"
              >
                Create Draft
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
